import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Typography from '../../components/Typography';
import OverviewCustomCard1 from '../../components/OverviewCustomCard1';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getTimezoneOffset } from 'date-fns-tz';
import moment from 'moment-timezone';
import CustomModal from '../../components/CustomModal';

// Subcomponent for each notification row
const NotificationRow = ({ notifications, notification, setNotifications, index, activeIndex, activeDropdown, handleClickNotification, setActiveDropdown }) => {
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    const toggleDropdown = (event) => {
      event.stopPropagation();
      if (activeDropdown === index) {
        setActiveDropdown(null);
      } else {
        setActiveDropdown(index);
      }
    };

    const button = buttonRef.current;
    if (button) {
      button.addEventListener('click', toggleDropdown);
    }

    window.addEventListener('click', handleClickOutside);

    return () => {
      if (button) {
        button.removeEventListener('click', toggleDropdown);
      }
      window.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown, index, setActiveDropdown]);

  const handleResendNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/resend`, { id: notification._id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveDropdown(null);
      handleClickNotification(); // Refresh active index if needed
      // Update UI or state to reflect resend (e.g., update status)
      const updatedNotifications = notifications.map(n =>
        n._id === notification._id ? { ...n, status: 'Resent' } : n
      );
      setNotifications(updatedNotifications);
    } catch (error) {
      console.error('Error resending notification:', error);
    }
  };

  const handleDeleteNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notification._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveDropdown(null);
      handleClickNotification(); // Refresh active index if needed
      setNotifications(notifications.filter(n => n._id !== notification._id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <tr
      key={notification._id || index}
      onClick={handleClickNotification}
      className={`border-b py-4 border-[#151515] h-[50px] text-white ${index === activeIndex ? 'bg-[#DF0F11]' : ''}`}
    >
      <td className="px-6 w-[35%]">
        <Typography text={notification.name || 'N/A'} size={'14px'} weight={400} />
      </td>
      <td className="w-[15%]">
        <Typography text={notification.status || 'N/A'} size={'14px'} weight={400} />
      </td>
      <td className="w-[10%]">
        <Typography text={notification.opened || '-'} size={'14px'} weight={400} />
      </td>
      <td className="w-[20%]">
        <Typography text={notification.delivery || 'N/A'} weight={400} />
      </td>
      <td className="w-[10%]">
        <Typography text={'< ' + notification.sends || 0} size={'14px'} weight={400} />
      </td>
      <td className="w-[10%]">
        <div className="relative">
          <button ref={buttonRef} className="w-5 h-5 cursor-pointer flex justify-center mx-auto items-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <mask id="mask0_579_391" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                <rect width="20" height="20" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_579_391)">
                <path d="M10 16.0577C9.65625 16.0577 9.36201 15.9353 9.11729 15.6904C8.87243 15.4457 8.75 15.1515 8.75 14.8077C8.75 14.464 8.87243 14.1697 9.11729 13.9248C9.36201 13.6801 9.65625 13.5577 10 13.5577C10.3438 13.5577 10.638 13.6801 10.8827 13.9248C11.1276 14.1697 11.25 14.464 11.25 14.8077C11.25 15.1515 11.1276 15.4457 10.8827 15.6904C10.638 15.9353 10.3438 16.0577 10 16.0577ZM10 11.25C9.65625 11.25 9.36201 11.1276 9.11729 10.8827C8.87243 10.638 8.75 10.3438 8.75 10C8.75 9.65625 8.87243 9.36201 9.11729 9.11729C9.36201 8.87243 9.65625 8.75 10 8.75C10.3438 8.75 10.638 8.87243 10.8827 9.11729C11.1276 9.36201 11.25 9.65625 11.25 10C11.25 10.3438 11.1276 10.638 10.8827 10.8827C10.638 11.1276 10.3438 11.25 10 11.25ZM10 6.44229C9.65625 6.44229 9.36201 6.31993 9.11729 6.07521C8.87243 5.83035 8.75 5.53604 8.75 5.19229C8.75 4.84854 8.87243 4.55431 9.11729 4.30958C9.36201 4.06472 9.65625 3.94229 10 3.94229C10.3438 3.94229 10.638 4.06472 10.8827 4.30958C11.1276 4.55431 11.25 4.84854 11.25 5.19229C11.25 5.53604 11.1276 5.83035 10.8827 6.07521C10.638 6.31993 10.3438 6.44229 10 6.44229Z" fill="white" />
              </g>
            </svg>
          </button>
          <div
            ref={dropdownRef}
            className={`z-50 absolute top-2 right-0 my-4 text-base list-none bg-[#121212] divide-y divide-gray-100 rounded-lg shadow-sm animate-backdropFadeIn animate-modalSlideIn ${activeDropdown === index ? '' : 'hidden'}`}
          >
            <ul className="py-2 text-center" aria-labelledby="user-menu-button">
              <li>
                <button
                  onClick={handleResendNotification}
                  className="block px-4 w-full py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
                >
                  <Typography text="Resend" family="Supply" transform="uppercase" />
                </button>
              </li>
              <li>
                <button
                  onClick={handleDeleteNotification}
                  className="block px-4 py-2 w-full text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
                >
                  <Typography text="Delete" family="Supply" transform="uppercase" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </td>
    </tr>
  );
};

const Notifications = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [newNotification, setNewNotification] = useState({
    name: '',
    message: '',
    sendsTo: 'investors',
    date: null,
    time: null,
  });
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [openCustomModal, setOpenCustomModal] = useState(false);
  const [customModalType, setCustomModalType] = useState('');
  const [customModalMessage, setCustomModalMessage] = useState('');
  const [customModalTitle, setCustomModalTitle] = useState('');
  const [customModalButtonText, setCustomModalButtonText] = useState('');
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [totalInvestments, setTotalInvestments] = useState(0);
  const [investmentReturns, setInvestmentReturns] = useState(0);
  const [averageROI, setAverageROI] = useState(0);
  const [sort, setSort] = useState('newest');
  const [activeDropdown, setActiveDropdown] = useState(null);

  const timezoneMapping = {
    'America/New_York': { name: 'Eastern Time', abbr: 'EST' },
    'America/Chicago': { name: 'Central Time', abbr: 'CST' },
    'America/Denver': { name: 'Mountain Time', abbr: 'MST' },
    'America/Los_Angeles': { name: 'Pacific Time', abbr: 'PST' },
    'Asia/Kolkata': { name: 'India Time', abbr: 'IST' },
    'Europe/London': { name: 'British Time', abbr: 'GMT' },
    'Asia/Tokyo': { name: 'Japan Time', abbr: 'JST' },
    'Australia/Sydney': { name: 'Sydney Time', abbr: 'AEST' },
    'UTC': { name: 'Coordinated Universal Time', abbr: 'UTC' },
  };

  const getTimezoneInfo = (timezone) => {
    const mapping = timezoneMapping[timezone] || { name: timezone, abbr: timezone.split('/')[1] || timezone };
    const date = new Date();
    const offsetMinutes = getTimezoneOffset(timezone, date) / 60;
    const offsetHours = offsetMinutes / 60;
    const offsetSign = offsetHours >= 0 ? '+' : '-';
    const offsetAbs = Math.abs(offsetHours);
    const offsetFormatted = `${offsetSign}${Math.floor(offsetAbs)}:${(offsetAbs % 1) * 60 || '00'}`;
    return {
      name: mapping.name,
      abbr: mapping.abbr,
      offset: offsetFormatted,
    };
  };

  const timezoneInfo = getTimezoneInfo(userTimezone);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(response.data.notifications || []);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchNotifications();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data.users || []);
        setInvestments(response.data.investments || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNotification((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setNewNotification((prev) => ({
      ...prev,
      date: date ? date : null,
    }));
  };

  const handleTimeChange = (time) => {
    if (!time) {
      setNewNotification((prev) => ({
        ...prev,
        time: null,
      }));
      return;
    }

    const timeMoment = moment.tz(time, userTimezone);
    const formattedTime = timeMoment.format('hh:mm A');
    const abbr = timeMoment.format('z');
    const offset = timeMoment.format('Z');
    const timezoneString = `(${abbr} ${offset}) ${timezoneInfo.name}`;

    const displayTime = `${formattedTime} ${timezoneString}`;

    setNewNotification((prev) => ({
      ...prev,
      time: {
        value: formattedTime,
        timezone: timezoneString,
        raw: time.toISOString(),
        display: displayTime,
      },
    }));
  };

  const handleSubmit = async (e) => {
    if (!newNotification.name || !newNotification.message || !newNotification.date || !newNotification.time) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const notificationData = {
        name: newNotification.name,
        message: newNotification.message,
        sendsTo: newNotification.sendsTo,
        date: newNotification.date ? moment(newNotification.date.toISOString()).format('YYYY-MM-DD') : '',
        time: newNotification.time.value,
        timezone: newNotification.time.timezone,
        delivery: newNotification.time.value + ' | ' + moment(newNotification.date.toISOString()).format('DD MMM YY'),
      };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, notificationData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenCustomModal(true);
      setCustomModalButtonText('Return to Dashboard');
      setCustomModalTitle('NOTIFICATION sent!');
      setCustomModalMessage('Notifications successfully sent. You may now close this screen.');
      setCustomModalType('success');

      setNotifications([response.data, ...notifications]);
      setNewNotification({
        name: '',
        message: '',
        sendsTo: 'investors',
        date: null,
        time: null,
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleResendNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/resend`, { id: notificationId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveDropdown(null);
      // handleClickNotification(); // Refresh active index if needed
      // Update UI or state to reflect resend (e.g., update status)
      const updatedNotifications = notifications.map(n =>
        n._id === notificationId ? { ...n, status: 'Resent' } : n
      );
      setNotifications(updatedNotifications);
    } catch (error) {
      console.error('Error resending notification:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveDropdown(null);
      // handleClickNotification(); // Refresh active index if needed
      setNotifications(notifications.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(3)}M`;
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands % 1 === 0 ? `$${thousands}K` : `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  useEffect(() => {
    if (investments.length > 0) {
      const totalInv = investments.reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
      setTotalInvestments(totalInv);

      const totalReturns = investments.reduce((sum, inv) => {
        const payouts = inv.payouts || [];
        const payoutSum = payouts.reduce((pSum, payout) => pSum + (payout.amount || 0), 0);
        return sum + payoutSum;
      }, 0);
      setInvestmentReturns(totalReturns);

      const avgROI = investments.length > 0
        ? (investments.reduce((sum, inv) => sum + (inv.annualROI || 0), 0) / investments.length).toFixed(1)
        : 0;
      setAverageROI(avgROI);
    }
  }, [investments]);

  const handleSortBy = () => {
    setSort(prev => prev === 'newest' ? 'oldest' : 'newest');
    const sortedNotifications = [...notifications].sort((a, b) =>
      sort === 'newest' ? new Date(a.createdAt) - new Date(b.createdAt) : new Date(b.createdAt) - new Date(a.createdAt)
    );
    setNotifications(sortedNotifications);
  };

  return (
    <>
      <div className="w-full md:p-5 p-4 rounded-[4px] bg-black">
        {openCustomModal && <CustomModal type={customModalType} title={customModalTitle} message={customModalMessage} onClose={() => setOpenCustomModal(false)} onConfirm={() => { if (customModalTitle === 'NOTIFICATION sent!') { navigate('/admin/notifications'); } if (customModalTitle === 'READY To SEND?') { handleSubmit(); } setOpenCustomModal(false); }} closeText={customModalButtonText} />}
        {openReviewModal && <div className="fixed fade inset-0 z-50 flex items-center h-full justify-center bg-black bg-opacity-60 animate-backdropFadeIn">
          <div className="relative md:w-[464px] mx-auto animate-modalSlideIn">
            <svg xmlns="http://www.w3.org/2000/svg" className='md:block hidden' width="464" height="702" viewBox="0 0 464 702" fill="none">
              <path d="M0 57.4186L41.0651 0H464V702H0V57.4186Z" fill="#1B1B1B" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" className='md:hidden block' width="340" height="702" viewBox="0 0 340 702" fill="none">
              <path d="M0 57.4186L41.0651 0H339.5V702H0V57.4186Z" fill="#1B1B1B"/>
            </svg>
            <div className="absolute inset-0 py-[42px] md:px-[66px] px-4 items-center text-white">
              <button
                onClick={() => setOpenReviewModal(false)}
                className="absolute top-6 right-6 text-white text-lg hover:text-gray-300"
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                  <g opacity="0.6">
                    <mask id="mask0_1769_5697" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="30" height="30">
                      <rect width="30" height="30" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1769_5697)">
                      <path d="M8.00078 23.317L6.68359 21.9998L13.6836 14.9998L6.68359 7.99981L8.00078 6.68262L15.0008 13.6826L22.0008 6.68262L23.318 7.99981L16.318 14.9998L23.318 21.9998L22.0008 23.317L15.0008 16.317L8.00078 23.317Z" fill="white" />
                    </g>
                  </g>
                </svg>
              </button>
              <Typography text={'SEND notification'} size={'27.752px'} weight={450} transform={'uppercase'} family={'Supply'} color={'#FFF'} lineheight={'60px'} />
              <form onSubmit={() => {
                setOpenReviewModal(false);
                setOpenCustomModal(true);
                setCustomModalType('confirm');
                setCustomModalTitle('READY To SEND?');
                setCustomModalButtonText('Correct send');
                setCustomModalMessage(<><span className='text-[#848484]'>Notification is sending to <br /></span><span className='text-white'>| {users && users.length} Investors |<br /></span><span className='text-[#8E8E8E]'>Is this correct?<br /></span></>);
              }} className="space-y-5">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={newNotification.name}
                    onChange={handleInputChange}
                    style={{ fontFamily: 'Aeonik' }}
                    className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-4 focus:outline-none ${!newNotification.name ? 'opacity-40' : 'opacity-100'} text-[14px]`}
                    placeholder="Notification Title"
                    required
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={newNotification.message}
                    rows={7}
                    onChange={handleInputChange}
                    style={{ fontFamily: 'Aeonik' }}
                    className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-4 focus:outline-none ${!newNotification.message && 'opacity-40'} text-[14px] resize-none`}
                    placeholder="Write message here"
                    required
                  />
                </div>
                <Typography text={'scheduling'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
                <Typography text={'Sends to your investors'} color={'#FFF'} size={'14px'} weight={400} lineheight={'170%'} />
                <div className="relative">
                  <DatePicker
                    selected={newNotification.date}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="absolute top-3 right-0" width="22" height="23" viewBox="0 0 22 23" fill="none">
                      <g opacity={newNotification.date ? '1' : '0.4'}>
                        <mask id="mask0_1797_7527" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="22" height="23">
                          <rect x="0.0546875" y="0.212402" width="21.9469" height="21.9469" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_1797_7527)">
                          <path d="M4.62578 20.3302C4.12283 20.3302 3.69228 20.1511 3.33412 19.793C2.97596 19.4348 2.79688 19.0043 2.79688 18.5013V5.69895C2.79688 5.196 2.97596 4.76545 3.33412 4.40729C3.69228 4.04913 4.12283 3.87005 4.62578 3.87005H5.54024V2.04114H7.36915V3.87005H14.6848V2.04114H16.5137V3.87005H17.4281C17.9311 3.87005 18.3616 4.04913 18.7198 4.40729C19.078 4.76545 19.2571 5.196 19.2571 5.69895V18.5013C19.2571 19.0043 19.078 19.4348 18.7198 19.793C18.3616 20.1511 17.9311 20.3302 17.4281 20.3302H4.62578ZM4.62578 18.5013H17.4281V9.35677H4.62578V18.5013ZM4.62578 7.52786H17.4281V5.69895H4.62578V7.52786Z" fill="white" />
                        </g>
                      </g>
                    </svg>}
                    showIcon={true}
                    toggleCalendarOnIconClick={true}
                    onChange={handleDateChange}
                    dateFormat="dd/MM/yyyy"
                    className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-2 focus:outline-none ${!newNotification.date ? 'opacity-40' : ''} text-[14px]`}
                    placeholderText="Date"
                    style={{ fontFamily: 'Aeonik' }}
                    required
                  />
                </div>
                <div className="relative">
                  <DatePicker
                    selected={newNotification.time?.raw ? new Date(newNotification.time.raw) : null}
                    value={newNotification.time?.display}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="absolute top-3 right-0" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <g opacity={newNotification.time?.raw ? 1 : 0.4}>
                        <mask id="mask0_579_387" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                          <rect width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_579_387)">
                          <path d="M12.7513 13.9165L13.918 12.7498L10.8346 9.6665V5.83317H9.16797V10.3332L12.7513 13.9165ZM10.0013 18.3332C8.84852 18.3332 7.76519 18.1144 6.7513 17.6769C5.73741 17.2394 4.85547 16.6457 4.10547 15.8957C3.35547 15.1457 2.76172 14.2637 2.32422 13.2498C1.88672 12.2359 1.66797 11.1526 1.66797 9.99984C1.66797 8.84706 1.88672 7.76373 2.32422 6.74984C2.76172 5.73595 3.35547 4.854 4.10547 4.104C4.85547 3.354 5.73741 2.76025 6.7513 2.32275C7.76519 1.88525 8.84852 1.6665 10.0013 1.6665C11.1541 1.6665 12.2374 1.88525 13.2513 2.32275C14.2652 2.76025 15.1471 3.354 15.8971 4.104C16.6471 4.854 17.2409 5.73595 17.6784 6.74984C18.1159 7.76373 18.3346 8.84706 18.3346 9.99984C18.3346 11.1526 18.1159 12.2359 17.6784 13.2498C17.2409 14.2637 16.6471 15.1457 15.8971 15.8957C15.1471 16.6457 14.2652 17.2394 13.2513 17.6769C12.2374 18.1144 11.1541 18.3332 10.0013 18.3332ZM10.0013 16.6665C11.8485 16.6665 13.4214 16.0172 14.7201 14.7186C16.0187 13.42 16.668 11.8471 16.668 9.99984C16.668 8.15262 16.0187 6.5797 14.7201 5.28109C13.4214 3.98248 11.8485 3.33317 10.0013 3.33317C8.15408 3.33317 6.58116 3.98248 5.28255 5.28109C3.98394 6.5797 3.33464 8.15262 3.33464 9.99984C3.33464 11.8471 3.98394 13.42 5.28255 14.7186C6.58116 16.0172 8.15408 16.6665 10.0013 16.6665Z" fill="white" />
                        </g>
                      </g>
                    </svg>}
                    showIcon={true}
                    toggleCalendarOnIconClick={true}
                    onChange={handleTimeChange}
                    timeFormat="h:mm aa"
                    showTimeSelect={true}
                    showTimeSelectOnly={true}
                    dateFormat="h:mm aa"
                    className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-2 focus:outline-none ${!newNotification.time ? 'opacity-40' : ''} text-[14px]`}
                    placeholderText={newNotification.time?.display || "Time"}
                    style={{ fontFamily: 'Aeonik' }}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="bg-[#DF0F11] h-8 flex items-center justify-between w-full text-white px-4 py-2 rounded mt-4"
                >
                  <Typography color={'#FFF'} size={'13.168px'} weight={400} text={'Review Notification'} />
                  <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                    <mask id="mask0_1797_7511" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="23" height="23">
                      <rect x="0.6875" y="0.548706" width="21.9469" height="21.9469" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1797_7511)">
                      <path d="M7.31983 15.8658H13.2638V14.4941H7.31983V15.8658ZM7.31983 12.208H16.0071V10.8363H7.31983V12.208ZM7.31983 8.55015H16.0071V7.17847H7.31983V8.55015ZM5.54373 19.295C5.08178 19.295 4.69077 19.135 4.37071 18.8149C4.05065 18.4948 3.89062 18.1038 3.89062 17.6419V5.40237C3.89062 4.94042 4.05065 4.54942 4.37071 4.22936C4.69077 3.9093 5.08178 3.74927 5.54373 3.74927H17.7832C18.2452 3.74927 18.6362 3.9093 18.9563 4.22936C19.2763 4.54942 19.4363 4.94042 19.4363 5.40237V17.6419C19.4363 18.1038 19.2763 18.4948 18.9563 18.8149C18.6362 19.135 18.2452 19.295 17.7832 19.295H5.54373ZM5.54373 17.9233H17.7832V9.35677H5.54373V17.9233ZM5.54373 7.52786H17.4281V5.69895H5.54373V7.52786Z" fill="white" />
                    </g>
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
        }
        <Typography text={'OVERVIEW'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
        <div className='mt-[25px] flex sm:gap-[10px] gap-1 sm:justify-between justify-center sm:flex-nowrap flex-wrap'>
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_705)">
                <path opacity="0.2" d="M30.0014 7.5C25.4496 7.49973 21.0049 8.87997 17.254 11.4585C13.503 14.037 10.6224 17.6925 8.9923 21.9423C7.36224 26.1921 7.05946 30.8364 8.12394 35.2619C9.18842 39.6874 11.5701 43.686 14.9545 46.7297C16.3661 43.9521 18.519 41.6196 21.1749 39.9905C23.8307 38.3614 26.8857 37.4994 30.0014 37.5C28.1472 37.5 26.3346 36.9502 24.7929 35.92C23.2512 34.8899 22.0496 33.4257 21.34 31.7127C20.6304 29.9996 20.4448 28.1146 20.8065 26.296C21.1682 24.4775 22.0611 22.807 23.3722 21.4959C24.6834 20.1848 26.3538 19.2919 28.1724 18.9301C29.991 18.5684 31.876 18.7541 33.589 19.4636C35.3021 20.1732 36.7663 21.3748 37.7964 22.9165C38.8265 24.4582 39.3764 26.2708 39.3764 28.125C39.3764 30.6114 38.3886 32.996 36.6305 34.7541C34.8723 36.5123 32.4878 37.5 30.0014 37.5C33.1168 37.4992 36.1716 38.3609 38.8274 39.9895C41.4832 41.6182 43.6363 43.9502 45.0482 46.7273C48.4318 43.6835 50.8127 39.6851 51.8766 35.26C52.9406 30.8349 52.6376 26.1912 51.0077 21.9419C49.3777 17.6926 46.4974 14.0375 42.7471 11.4591C38.9967 8.88069 34.5526 7.50025 30.0014 7.5Z" fill="white" />
                <path d="M14.9531 46.7273C16.3639 43.9486 18.5165 41.6148 21.1724 39.9846C23.8283 38.3544 26.8837 37.4915 30 37.4915C33.1163 37.4915 36.1717 38.3544 38.8276 39.9846C41.4835 41.6148 43.6361 43.9486 45.0469 46.7273" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 52.5C42.4264 52.5 52.5 42.4264 52.5 30C52.5 17.5736 42.4264 7.5 30 7.5C17.5736 7.5 7.5 17.5736 7.5 30C7.5 42.4264 17.5736 52.5 30 52.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 37.5C35.1777 37.5 39.375 33.3027 39.375 28.125C39.375 22.9473 35.1777 18.75 30 18.75C24.8223 18.75 20.625 22.9473 20.625 28.125C20.625 33.3027 24.8223 37.5 30 37.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_705">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Registered users'}
            value={users.length ?? 0}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_950)">
                <path opacity="0.2" d="M41.2498 22.6688V29.0625C41.2498 33.2813 34.3967 36.7617 25.4365 37.3969C28.6967 38.632 32.9061 39.375 37.4998 39.375C47.8545 39.375 56.2498 35.5969 56.2498 30.9375C56.2498 26.8547 49.8092 23.4516 41.2498 22.6688Z" fill="white" />
                <path opacity="0.2" d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" fill="white" />
                <path d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.75 19.6875V29.0625C3.75 33.7219 12.1453 37.5 22.5 37.5C32.8547 37.5 41.25 33.7219 41.25 29.0625V19.6875" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15 27.4219V36.7969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M41.2498 22.6688C49.8092 23.4516 56.2498 26.8547 56.2498 30.9375C56.2498 35.5969 47.8545 39.375 37.4998 39.375C32.9061 39.375 28.6967 38.632 25.4365 37.3969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M18.75 37.3312V40.3125C18.75 44.9719 27.1453 48.75 37.5 48.75C47.8547 48.75 56.25 44.9719 56.25 40.3125V30.9375" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M45 38.6719V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 27.4219V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_950">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Total Investments'}
            value={formatCurrency(totalInvestments)}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_961)">
                <path opacity="0.2" d="M7.5 11.25H48.75C49.7446 11.25 50.6984 11.6451 51.4016 12.3483C52.1049 13.0516 52.5 14.0054 52.5 15V48.75H7.5V11.25Z" fill="white" />
                <path d="M52.5 48.75H7.5V11.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M46.875 16.875L30 33.75L22.5 26.25L7.5 41.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M46.875 26.25V16.875H37.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_961">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Investment Returns'}
            value={formatCurrency(investmentReturns)}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_967)">
                <path opacity="0.2" d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1562 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8438 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1562 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1562 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1562 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8438 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" fill="white" />
                <path d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1563 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8437 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1563 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1563 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1563 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8437 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.5 26.25C24.5711 26.25 26.25 24.5711 26.25 22.5C26.25 20.4289 24.5711 18.75 22.5 18.75C20.4289 18.75 18.75 20.4289 18.75 22.5C18.75 24.5711 20.4289 26.25 22.5 26.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M37.5 41.25C39.5711 41.25 41.25 39.5711 41.25 37.5C41.25 35.4289 39.5711 33.75 37.5 33.75C35.4289 33.75 33.75 35.4289 33.75 37.5C33.75 39.5711 35.4289 41.25 37.5 41.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20.625 39.375L39.375 20.625" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_967">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Average ROI'}
            value={`${averageROI}%`}
          />
        </div>
      </div>
      <div className="w-full mt-[14px] lg:p-5 lg:bg-black rounded-[4px]  ">
        <div className="grid grid-cols-12 lg:gap-0 gap-8">
          <div className="lg:col-span-8 col-span-12 lg:pr-8 max-lg:p-5 lg:border-r border-opacity-20 border-white rounded-[4px] bg-black">
            <div className="flex justify-between items-center">
              <Typography text={'Manage notifications'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
              <div onClick={handleSortBy} className="flex cursor-pointer justify-between gap-10.5 rounded border border-white items-center h-8 w-32 p-[10px]">
                <div className="flex items-center gap-2">
                  <div className="w-[14px] h-[14px] flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <mask id="mask0_498_834" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                        <rect width="14" height="14" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_498_834)">
                        <path d="M4.90233 7.38136V3.13426L3.24873 4.78786L2.625 4.17303L5.33969 1.45834L8.05452 4.17303L7.43079 4.78786L5.77719 3.13426V7.38136H4.90233ZM8.65463 12.5417L5.93994 9.82699L6.56367 9.21216L8.21713 10.8658V6.61866H9.09213V10.8658L10.7457 9.21216L11.3693 9.82699L8.65463 12.5417Z" fill="white" />
                      </g>
                    </svg>
                  </div>
                  <Typography color={'#FFF'} size={'12px'} lineheight={'170%'} weight={400} text={'Sort By'} />
                </div>
                <div className="w-[10px] h-[10px] flex-shrink-0">
                  {sort === 'newest' ? <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <mask id="mask0_498_838" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                      <rect x="10" width="10" height="10" transform="rotate(90 10 0)" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_498_838)">
                      <path d="M0.834635 3.3431L1.57422 2.60352L5.0013 6.0306L8.42839 2.60352L9.16797 3.3431L5.0013 7.50977L0.834635 3.3431Z" fill="white" />
                    </g>
                  </svg> : <svg xmlns="http://www.w3.org/2000/svg" className='rotate-180' width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <mask id="mask0_498_838" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                      <rect x="10" width="10" height="10" transform="rotate(90 10 0)" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_498_838)">
                      <path d="M0.834635 3.3431L1.57422 2.60352L5.0013 6.0306L8.42839 2.60352L9.16797 3.3431L5.0013 7.50977L0.834635 3.3431Z" fill="white" />
                    </g>
                  </svg>}
                </div>
              </div>
            </div>
            <div className="relative h-[516px] scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto md:hidden block overflow-x-auto mt-3">
              {(notifications || []).map((notification, index) => {

                return (
                  <div
                    key={index}
                    className="border-b flex-col flex items-start border-gray-700 py-5 cursor-pointer"
                    onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  >
                    <div className="flex flex-col  items-start self-stretch">
                      {/* <Typography text={ticket.ticketNo} opacity={0.6} size={'14px'} color='#FFF' /> */}
                      <div className="flex justify-between items-center self-stretch">
                        <Typography text={notification?.name || 'Unknown'} size={'14px'} color='#FFF' weight={400} />
                        {activeIndex === index ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <mask id="mask0_4672_17113" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                            <rect width="14" height="14" transform="matrix(-4.37114e-08 1 1 4.37114e-08 0 3.05176e-05)" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_4672_17113)">
                            <path d="M12.6318 4.67558L11.804 3.84769L7.00049 8.65115L2.19704 3.84769L1.36914 4.67558L7.00049 10.3069L12.6318 4.67558Z" fill="#DF0F11" />
                          </g>
                        </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <mask id="mask0_4672_17226" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                            <rect width="14" height="14" transform="matrix(1.19249e-08 -1 -1 -1.19249e-08 14 14.0001)" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_4672_17226)">
                            <path d="M1.36815 9.32454L2.19605 10.1524L6.99951 5.34898L11.803 10.1524L12.6309 9.32454L6.99951 3.69318L1.36815 9.32454Z" fill="#DF0F11" />
                          </g>
                        </svg>}
                      </div>
                    </div>

                    {/* Optional expanded content */}
                    {activeIndex === index && (
                      <div className="flex mt-4 translate-y-0 flex-col gap-5 justify-center items-start self-stretch animation transition-all animate-backdropFadeIn">
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Status'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={notification.status} size={'14px'} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Opened'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={notification?.opened || '-'} size={'14px'} weight={400} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Delivery'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={notification?.delivery} size={'14px'} color='#FFF' weight={400} />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Sends'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={'< ' + notification.sends || 0} color='#FFF' size={'14px'} weight={400} />
                        </div>
                        <div className='flex items-center self-stretch h-full gap-[6px]'>
                          <button
                            onClick={() => handleResendNotification(notification._id)}
                            className="flex flex-1 gap-[10px] rounded opacity-60 border bg-black border-white text-white justify-center items-center px-[18px] py-[10px]"
                          >
                            <Typography text="Resend" size={'12px'} family="Supply" transform="uppercase" />
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="13" viewBox="0 0 16 13" fill="none">
                              <path d="M0.828125 12.542V0.458672L15.171 6.50034L0.828125 12.542ZM2.07812 10.667L11.9531 6.50034L2.07812 2.33367V5.41055L6.59729 6.50034L2.07812 7.59013V10.667Z" fill="white" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteNotification(notification._id)}
                            className="flex flex-1 gap-[10px] rounded opacity-60 border bg-black border-[#DF0F11] text-white justify-center items-center px-[18px] py-[10px]"
                          >
                            <Typography text="Delete" size={'12px'} family="Supply" transform="uppercase" />
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" viewBox="0 0 10 15" fill="none">
                              <path d="M2.09534 15.0002C1.76424 15.0002 1.48147 14.8501 1.24701 14.5498C1.01269 14.2493 0.895522 13.8869 0.895522 13.4626V2.28065H0V1.03738H3.20896V0.000213623H6.79104V1.03738H10V2.28065H9.10448V13.4523C9.10448 13.8945 8.98849 14.263 8.75653 14.5579C8.52456 14.8528 8.24061 15.0002 7.90466 15.0002H2.09534ZM8.13433 2.28065H1.86567V13.4626C1.86567 13.5485 1.88719 13.6191 1.93022 13.6742C1.97326 13.7294 2.0283 13.7569 2.09534 13.7569H7.90466C7.96213 13.7569 8.01474 13.7263 8.0625 13.6649C8.11039 13.6037 8.13433 13.5363 8.13433 13.4626V2.28065ZM3.51045 12.0355H4.48041V4.00209H3.51045V12.0355ZM5.51959 12.0355H6.48955V4.00209H5.51959V12.0355Z" fill="#DF0F11" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>)
              })}
            </div>
            <div className="md:block hidden relative overflow-x-auto mt-3">
              <table className="w-full text-sm text-left rtl:text-right table-fixed">
                <thead className="text-xs text-white opacity-60">
                  <tr className="border-b border-[#151515]">
                    <th scope="col" className={`px-6 w-[35%] py-3`}>
                      <Typography text={'Notification'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`w-[15%] py-3`}>
                      <Typography text={'Status'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`w-[10%] py-3`}>
                      <Typography text={'Opened'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`w-[20%] py-3`}>
                      <Typography text={'Delivery'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`w-[10%] py-3`}>
                      <Typography text={'Sends'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`w-[10%] py-3`}></th>
                  </tr>
                </thead>
              </table>
              <div className="relative h-[516px] scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm text-left rtl:text-right">
                  <tbody>
                    {(notifications || []).map((notification, index) => (
                      <NotificationRow
                        key={notification._id || index}
                        notifications={notifications}
                        notification={notification}
                        setNotifications={setNotifications}
                        handleClickNotification={() => setActiveIndex(index)}
                        index={index}
                        activeIndex={activeIndex}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 col-span-12 lg:px-6   max-lg:p-5 rounded-[4px] bg-black">
            <div className="mt-2">
              <form onSubmit={handleSubmit} className="lg:space-y-5 lg:block max-lg:gap-10 md:gap-10 max-sm:block max-sm:space-y-5 flex">
                <div className='lg:space-y-5 flex-1 gap-10'>
                  <div className="flex items-center">
                    <Typography text={'create new notification'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={newNotification.name}
                      onChange={handleInputChange}
                      style={{ fontFamily: 'Aeonik' }}
                      className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-4 focus:outline-none ${!newNotification.name ? 'opacity-40' : 'opacity-100'} text-[14px]`}
                      placeholder="Notification Title"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={newNotification.message}
                      rows={7}
                      onChange={handleInputChange}
                      style={{ fontFamily: 'Aeonik' }}
                      className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-4 focus:outline-none ${!newNotification.message && 'opacity-40'} text-[14px] resize-none`}
                      placeholder="Write message here"
                    />
                  </div>
                </div>
                <div className='space-y-5 flex-1'>
                  <Typography text={'scheduling'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
                  <Typography text={'Sends to your investors'} color={'#FFF'} size={'14px'} weight={400} lineheight={'170%'} />
                  <div className="relative">
                    <DatePicker
                      selected={newNotification.date}
                      icon={<svg xmlns="http://www.w3.org/2000/svg" className="absolute top-3 right-0" width="22" height="23" viewBox="0 0 22 23" fill="none">
                        <g opacity={newNotification.date ? '1' : '0.4'}>
                          <mask id="mask0_1797_7527" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="22" height="23">
                            <rect x="0.0546875" y="0.212402" width="21.9469" height="21.9469" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_1797_7527)">
                            <path d="M4.62578 20.3302C4.12283 20.3302 3.69228 20.1511 3.33412 19.793C2.97596 19.4348 2.79688 19.0043 2.79688 18.5013V5.69895C2.79688 5.196 2.97596 4.76545 3.33412 4.40729C3.69228 4.04913 4.12283 3.87005 4.62578 3.87005H5.54024V2.04114H7.36915V3.87005H14.6848V2.04114H16.5137V3.87005H17.4281C17.9311 3.87005 18.3616 4.04913 18.7198 4.40729C19.078 4.76545 19.2571 5.196 19.2571 5.69895V18.5013C19.2571 19.0043 19.078 19.4348 18.7198 19.793C18.3616 20.1511 17.9311 20.3302 17.4281 20.3302H4.62578ZM4.62578 18.5013H17.4281V9.35677H4.62578V18.5013ZM4.62578 7.52786H17.4281V5.69895H4.62578V7.52786Z" fill="white" />
                          </g>
                        </g>
                      </svg>}
                      showIcon={true}
                      toggleCalendarOnIconClick={true}
                      onChange={handleDateChange}
                      dateFormat="dd/MM/yyyy"
                      className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-2 focus:outline-none ${!newNotification.date ? 'opacity-40' : ''} text-[14px]`}
                      placeholderText="Date"
                      style={{ fontFamily: 'Aeonik' }}
                    />
                  </div>
                  <div className="relative">
                    <DatePicker
                      selected={newNotification.time?.raw ? new Date(newNotification.time.raw) : null}
                      value={newNotification.time?.display}
                      icon={<svg xmlns="http://www.w3.org/2000/svg" className="absolute top-3 right-0" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <g opacity={newNotification.time?.raw ? 1 : 0.4}>
                          <mask id="mask0_579_387" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                            <rect width="20" height="20" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_579_387)">
                            <path d="M12.7513 13.9165L13.918 12.7498L10.8346 9.6665V5.83317H9.16797V10.3332L12.7513 13.9165ZM10.0013 18.3332C8.84852 18.3332 7.76519 18.1144 6.7513 17.6769C5.73741 17.2394 4.85547 16.6457 4.10547 15.8957C3.35547 15.1457 2.76172 14.2637 2.32422 13.2498C1.88672 12.2359 1.66797 11.1526 1.66797 9.99984C1.66797 8.84706 1.88672 7.76373 2.32422 6.74984C2.76172 5.73595 3.35547 4.854 4.10547 4.104C4.85547 3.354 5.73741 2.76025 6.7513 2.32275C7.76519 1.88525 8.84852 1.6665 10.0013 1.6665C11.1541 1.6665 12.2374 1.88525 13.2513 2.32275C14.2652 2.76025 15.1471 3.354 15.8971 4.104C16.6471 4.854 17.2409 5.73595 17.6784 6.74984C18.1159 7.76373 18.3346 8.84706 18.3346 9.99984C18.3346 11.1526 18.1159 12.2359 17.6784 13.2498C17.2409 14.2637 16.6471 15.1457 15.8971 15.8957C15.1471 16.6457 14.2652 17.2394 13.2513 17.6769C12.2374 18.1144 11.1541 18.3332 10.0013 18.3332ZM10.0013 16.6665C11.8485 16.6665 13.4214 16.0172 14.7201 14.7186C16.0187 13.42 16.668 11.8471 16.668 9.99984C16.668 8.15262 16.0187 6.5797 14.7201 5.28109C13.4214 3.98248 11.8485 3.33317 10.0013 3.33317C8.15408 3.33317 6.58116 3.98248 5.28255 5.28109C3.98394 6.5797 3.33464 8.15262 3.33464 9.99984C3.33464 11.8471 3.98394 13.42 5.28255 14.7186C6.58116 16.0172 8.15408 16.6665 10.0013 16.6665Z" fill="white" />
                          </g>
                        </g>
                      </svg>}
                      showIcon={true}
                      toggleCalendarOnIconClick={true}
                      onChange={handleTimeChange}
                      timeFormat="h:mm aa"
                      showTimeSelect={true}
                      showTimeSelectOnly={true}
                      dateFormat="h:mm aa"
                      className={`w-full bg-transparent outline-none border-b border-[#FFF] border-opacity-40 text-white py-2 focus:outline-none ${!newNotification.time ? 'opacity-40' : ''} text-[14px]`}
                      placeholderText={newNotification.time?.display || "Time"}
                      style={{ fontFamily: 'Aeonik' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenReviewModal(true)}
                    className="bg-[#DF0F11] h-8 flex items-center justify-between w-full text-white px-4 py-2 rounded mt-4"
                  >
                    <Typography color={'#FFF'} size={'12px'} weight={400} text={'Review'} />
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                      <mask id="mask0_579_368" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="21" height="20">
                        <rect x="0.5" width="20" height="20" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_579_368)">
                        <path d="M6.54297 13.9583H11.9596V12.7083H6.54297V13.9583ZM6.54297 10.625H14.4596V9.37501H6.54297V10.625ZM6.54297 7.29167H14.4596V6.04167H6.54297V7.29167ZM4.92443 17.0833C4.50345 17.0833 4.14714 16.9375 3.85547 16.6458C3.5638 16.3542 3.41797 15.9979 3.41797 15.5769V4.42313C3.41797 4.00216 3.5638 3.64584 3.85547 3.35417C4.14714 3.06251 4.50345 2.91667 4.92443 2.91667H16.0782C16.4992 2.91667 16.8555 3.06251 17.1471 3.35417C17.4388 3.64584 17.5846 4.00216 17.5846 4.42313V15.5769C17.5846 15.9979 17.4388 16.3542 17.1471 16.6458C16.8555 16.9375 16.4992 17.0833 16.0782 17.0833H4.92443ZM4.92443 15.8333H16.0782C16.1423 15.8333 16.2011 15.8066 16.2544 15.7531C16.3079 15.6998 16.3346 15.641 16.3346 15.5769V4.42313C16.3346 4.35896 16.3079 4.30021 16.2544 4.24688C16.2011 4.19341 16.1423 4.16667 16.0782 4.16667H4.92443C4.86026 4.16667 4.80151 4.19341 4.74818 4.24688C4.69471 4.30021 4.66797 4.35896 4.66797 4.42313V15.5769C4.66797 15.641 4.69471 15.6998 4.74818 15.7531C4.80151 15.8066 4.86026 15.8333 4.92443 15.8333Z" fill="white" />
                      </g>
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Notifications;